import { COMPETITOR_RATING_EPSILON } from '@/core/components/reputation/competitor-map-pins';

export type StandingCompetitor = {
  id: string;
  name: string;
  rating?: number;
  reviewCount?: number;
};

export type StandingVolumeRow = {
  id: string;
  name: string;
  count: number;
  isYou: boolean;
};

export type CompetitorStanding = {
  /** How your rating compares to the tracked field. */
  /** `none`: you have no rating yet (no own reviews) — rating rank is unknown. */
  quality: 'ahead' | 'tied' | 'behind' | 'none';
  /** Where your review volume lands in the field. */
  volume: 'first' | 'mid' | 'last';
  ratingRank: number;
  ratingTotal: number;
  /** Another business shares your rating (rank is "shared"). */
  ratingShared: boolean;
  volumeRank: number;
  volumeTotal: number;
  /** Every rated business sits on your rating. */
  allTied: boolean;
  /** Field ranked by review count, you included. */
  volumeRows: StandingVolumeRow[];
};

/** Rank you against tracked competitors by rating and review volume. */
export function computeStanding(
  you: { name: string; rating?: number; reviewCount?: number },
  competitors: StandingCompetitor[],
): CompetitorStanding {
  const hasRating = you.rating != null;
  const yourRating = you.rating ?? 0;
  const rated = competitors.filter((c) => c.rating != null);
  const better = rated.filter(
    (c) => (c.rating ?? 0) - yourRating > COMPETITOR_RATING_EPSILON,
  ).length;
  const ratingShared =
    hasRating &&
    rated.some(
      (c) =>
        Math.abs((c.rating ?? 0) - yourRating) <= COMPETITOR_RATING_EPSILON,
    );

  const quality: CompetitorStanding['quality'] = !hasRating
    ? 'none'
    : better > 0
      ? 'behind'
      : ratingShared
        ? 'tied'
        : 'ahead';

  const volumeRows: StandingVolumeRow[] = [
    { id: 'you', name: you.name, count: you.reviewCount ?? 0, isYou: true },
    ...competitors
      .filter((c) => c.reviewCount != null)
      .map((c) => ({
        id: c.id,
        name: c.name,
        count: c.reviewCount ?? 0,
        isYou: false,
      })),
  ].sort((a, b) => b.count - a.count || Number(b.isYou) - Number(a.isYou));

  const volumeRank = volumeRows.findIndex((row) => row.isYou) + 1;
  const volumeTotal = volumeRows.length;
  const volume: CompetitorStanding['volume'] =
    volumeTotal > 1 && volumeRank === volumeTotal
      ? 'last'
      : volumeRank === 1
        ? 'first'
        : 'mid';

  return {
    quality,
    volume,
    ratingRank: hasRating ? better + 1 : 0,
    ratingTotal: rated.length + 1,
    ratingShared,
    volumeRank,
    volumeTotal,
    allTied:
      hasRating &&
      rated.length > 0 &&
      rated.every(
        (c) =>
          Math.abs((c.rating ?? 0) - yourRating) <= COMPETITOR_RATING_EPSILON,
      ),
    volumeRows,
  };
}
